import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { ErrorApi } from '@/api/errores'
import { clienteConsola } from './sesion'
import type {
  Ambiente,
  ColasTenant,
  ContingenciaRegistro,
  ContingenciaTenant,
  CrearTenant,
  EstadoTenant,
  EventoAuditoriaConsola,
  EventoAuditoriaTenant,
  ModoSoporte,
  Motor,
  PaginaTenants,
  PerfilEmisor,
  Plan,
  ResumenConsola,
  RolUsuario,
  SecuenciaTenant,
  SesionSoporteAbierta,
  TenantDetalle,
  TrabajoFallido,
  UsuarioTenant,
  CertificadoTenant,
  CertificadoCargado,
  ClaveGenerada,
  ClavesTenant,
} from './tipos'

/**
 * Hooks de la consola. La pantalla no sabe de HTTP: pide un hook y el hook
 * decide caché, red e invalidación (`docs/07`, «La regla que sostiene todo»).
 *
 * ## Escrituras sin clave de idempotencia, y por qué
 *
 * El contrato de la consola no define `Idempotency-Key`. Mandar una que el
 * servidor no honra sería peor que no mandarla: el transporte **reintenta** las
 * escrituras que llevan clave, y un reintento de «crear cliente» o de
 * «reinyectar» que el servidor no deduplica haría dos. Sin clave, una escritura
 * no se reintenta jamás; el doble clic lo frena el botón deshabilitado mientras
 * la petición está en vuelo.
 *
 * ## El motivo viaja siempre
 *
 * Toda escritura lleva `motivo`, también las que en el contrato no lo nombran
 * (perfil, alta de usuario, rol, credencial, secuencia, certificado, alta de
 * cliente). Si el servidor lo exige, está; si no lo lee, un campo de más en un
 * JSON no rompe nada. Al revés —que lo exija y no esté— sería un 400 en cada
 * acción.
 */

const ruta = (id: string) => `/consola/tenants/${encodeURIComponent(id)}`

export const clavesConsola = {
  todo: ['consola'] as const,
  resumen: ['consola', 'resumen'] as const,
  tenants: ['consola', 'tenants'] as const,
  listaTenants: (filtros: FiltrosTenants) => ['consola', 'tenants', filtros] as const,
  tenant: (id: string) => ['consola', 'tenant', id] as const,
  subrecurso: (id: string, nombre: string) => ['consola', 'tenant', id, nombre] as const,
  planes: ['consola', 'planes'] as const,
  fallidos: (limite: number) => ['consola', 'fallidos', limite] as const,
  auditoria: (tenant: string | undefined) => ['consola', 'auditoria', tenant ?? ''] as const,
}

/** Tras escribir sobre un contribuyente, todo lo que pudo cambiar se vuelve a pedir. */
function invalidarTenant(cliente: QueryClient, id: string): Promise<unknown> {
  return Promise.all([
    cliente.invalidateQueries({ queryKey: clavesConsola.tenant(id) }),
    cliente.invalidateQueries({ queryKey: clavesConsola.tenants }),
    cliente.invalidateQueries({ queryKey: clavesConsola.resumen }),
    cliente.invalidateQueries({ queryKey: ['consola', 'fallidos'] }),
    // La auditoría de la consola crece con cada escritura: verla desactualizada
    // justo después de actuar es la forma de que alguien dude de si quedó anotado.
    cliente.invalidateQueries({ queryKey: ['consola', 'auditoria'] }),
  ])
}

/* ---------------------------------------------------------------------- */
/* Lecturas                                                                */
/* ---------------------------------------------------------------------- */

export function useResumenConsola() {
  return useQuery({
    queryKey: clavesConsola.resumen,
    queryFn: ({ signal }) => clienteConsola.obtener<ResumenConsola>('/consola/resumen', { senal: signal }),
    refetchInterval: 60_000,
  })
}

export interface FiltrosTenants {
  buscar?: string
  estado?: string
  limite: number
  saltar: number
}

export function useTenants(filtros: FiltrosTenants) {
  return useQuery({
    queryKey: clavesConsola.listaTenants(filtros),
    queryFn: ({ signal }) =>
      clienteConsola.obtener<PaginaTenants>('/consola/tenants', {
        consulta: {
          buscar: filtros.buscar,
          estado: filtros.estado,
          limite: filtros.limite,
          saltar: filtros.saltar,
        },
        senal: signal,
      }),
    // Con más de 13 000 contribuyentes cada tecla es otra página: la anterior
    // se queda en pantalla mientras llega la nueva, en vez de parpadear a vacío.
    placeholderData: keepPreviousData,
  })
}

export function useTenant(id: string) {
  return useQuery({
    queryKey: clavesConsola.tenant(id),
    queryFn: ({ signal }) => clienteConsola.obtener<TenantDetalle>(ruta(id), { senal: signal }),
  })
}

export function useUsuariosTenant(id: string) {
  return useQuery({
    queryKey: clavesConsola.subrecurso(id, 'usuarios'),
    queryFn: ({ signal }) => clienteConsola.obtener<UsuarioTenant[]>(`${ruta(id)}/usuarios`, { senal: signal }),
  })
}

export function useClavesTenant(id: string) {
  return useQuery({
    queryKey: clavesConsola.subrecurso(id, 'claves'),
    queryFn: ({ signal }) => clienteConsola.obtener<ClavesTenant>(`${ruta(id)}/claves`, { senal: signal }),
  })
}

export function useSecuenciasTenant(id: string) {
  return useQuery({
    queryKey: clavesConsola.subrecurso(id, 'secuencias'),
    queryFn: ({ signal }) =>
      clienteConsola.obtener<SecuenciaTenant[]>(`${ruta(id)}/secuencias`, { senal: signal }),
  })
}

export function useCertificadosTenant(id: string) {
  return useQuery({
    queryKey: clavesConsola.subrecurso(id, 'certificados'),
    queryFn: ({ signal }) =>
      clienteConsola.obtener<CertificadoTenant[]>(`${ruta(id)}/certificados`, { senal: signal }),
  })
}

export function useColasTenant(id: string) {
  return useQuery({
    queryKey: clavesConsola.subrecurso(id, 'colas'),
    queryFn: ({ signal }) => clienteConsola.obtener<ColasTenant>(`${ruta(id)}/colas`, { senal: signal }),
    refetchInterval: 30_000,
  })
}

/**
 * El contrato dice «la vigente o `null`, más las últimas 20» sin fijar los
 * nombres. Se lee `vigente` y la lista de `ultimas`, y se aceptan también
 * `historial`/`recientes` o una lista suelta, para no enseñar «sin historial»
 * por un nombre de campo. **Punto a verificar contra el extremo.**
 */
function normalizarContingencia(crudo: unknown): ContingenciaTenant {
  if (Array.isArray(crudo)) {
    const lista = crudo as ContingenciaRegistro[]
    return { vigente: lista.find((c) => !c.fin) ?? null, ultimas: lista }
  }
  if (crudo === null || crudo === undefined || typeof crudo !== 'object') return { vigente: null, ultimas: [] }
  const objeto = crudo as Record<string, unknown>
  const lista = (objeto.ultimas ?? objeto.historial ?? objeto.recientes ?? []) as ContingenciaRegistro[]
  return {
    vigente: (objeto.vigente as ContingenciaRegistro | null | undefined) ?? null,
    ultimas: Array.isArray(lista) ? lista : [],
  }
}

export function useContingenciaTenant(id: string) {
  return useQuery({
    queryKey: clavesConsola.subrecurso(id, 'contingencia'),
    queryFn: async ({ signal }) =>
      normalizarContingencia(await clienteConsola.obtener<unknown>(`${ruta(id)}/contingencia`, { senal: signal })),
  })
}

export function usePlanes() {
  return useQuery({
    queryKey: clavesConsola.planes,
    queryFn: ({ signal }) => clienteConsola.obtener<Plan[]>('/consola/planes', { senal: signal }),
    staleTime: 600_000,
  })
}

export function useFallidos(limite: number) {
  return useQuery({
    queryKey: clavesConsola.fallidos(limite),
    queryFn: ({ signal }) =>
      clienteConsola.obtener<TrabajoFallido[]>('/consola/colas/fallidos', { consulta: { limite }, senal: signal }),
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  })
}

const PAGINA_AUDITORIA = 100

/**
 * La auditoría se pagina hacia atrás en el tiempo: `antesDe` es la fecha del
 * último evento que ya se tiene. Una página que llega corta es la última.
 */
export function useAuditoriaConsola(tenant: string | undefined) {
  return useInfiniteQuery({
    queryKey: clavesConsola.auditoria(tenant),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      clienteConsola.obtener<EventoAuditoriaConsola[]>('/consola/auditoria', {
        consulta: { tenant, antesDe: pageParam, limite: PAGINA_AUDITORIA },
        senal: signal,
      }),
    getNextPageParam: (ultima) =>
      ultima.length < PAGINA_AUDITORIA ? undefined : ultima[ultima.length - 1]?.ocurridoEl,
  })
}

export function useAuditoriaTenant(id: string) {
  return useInfiniteQuery({
    queryKey: clavesConsola.subrecurso(id, 'auditoria'),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      clienteConsola.obtener<EventoAuditoriaTenant[]>(`${ruta(id)}/auditoria`, {
        consulta: { antesDe: pageParam, limite: PAGINA_AUDITORIA },
        senal: signal,
      }),
    getNextPageParam: (ultima) =>
      ultima.length < PAGINA_AUDITORIA ? undefined : ultima[ultima.length - 1]?.ocurridoEl,
  })
}

/* ---------------------------------------------------------------------- */
/* Escrituras                                                              */
/* ---------------------------------------------------------------------- */

/**
 * Una escritura sobre un contribuyente: la hace y refresca lo que pudo cambiar.
 * Sin reintento (ver arriba).
 */
function useEscrituraTenant<V>(id: string, hacer: (variables: V) => Promise<unknown>) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: hacer,
    onSuccess: () => invalidarTenant(cliente, id),
  })
}

/**
 * Alta de cliente. Fuera de `useMutation` porque puede llevar la credencial
 * inicial del administrador, y la caché de mutaciones conserva las variables
 * mientras la pantalla vive. Quien llama refresca con `useRefrescarListado`.
 */
export function crearTenant(datos: CrearTenant, motivo: string): Promise<{ id: string }> {
  return clienteConsola.crear<{ id: string }>('/consola/tenants', { cuerpo: { ...datos, motivo } })
}

export function useRefrescarListado(): () => Promise<unknown> {
  const cliente = useQueryClient()
  return () =>
    Promise.all([
      cliente.invalidateQueries({ queryKey: clavesConsola.tenants }),
      cliente.invalidateQueries({ queryKey: clavesConsola.resumen }),
      cliente.invalidateQueries({ queryKey: ['consola', 'auditoria'] }),
    ])
}

export function useActualizarPerfil(id: string) {
  return useEscrituraTenant(id, (v: { perfil: PerfilEmisor; motivo: string }) =>
    clienteConsola.reemplazar(`${ruta(id)}/perfil`, { cuerpo: { ...v.perfil, motivo: v.motivo } }),
  )
}

export function useCambiarEstado(id: string) {
  return useEscrituraTenant(id, (v: { estado: EstadoTenant; motivo: string }) =>
    clienteConsola.reemplazar(`${ruta(id)}/estado`, { cuerpo: v }),
  )
}

export function useCambiarAmbiente(id: string) {
  return useEscrituraTenant(id, (v: { ambiente: Ambiente; motivo: string }) =>
    clienteConsola.reemplazar(`${ruta(id)}/ambiente`, { cuerpo: v }),
  )
}

export function useCambiarMotor(id: string) {
  return useEscrituraTenant(id, (v: { motor: Motor; proveedor?: string; motivo: string }) =>
    clienteConsola.reemplazar(`${ruta(id)}/motor`, { cuerpo: v }),
  )
}

export function useCambiarPlan(id: string) {
  return useEscrituraTenant(id, (v: { planId: string; motivo: string }) =>
    clienteConsola.reemplazar(`${ruta(id)}/plan`, { cuerpo: v }),
  )
}

/**
 * Un complemento del cliente: la nómina (`docs/adr/0018`) y la multisucursal
 * de la caja (migración 0027), y la contabilidad (`docs/adr/0025`). Son
 * independientes: encender uno no pide el otro.
 */
export interface ComplementoConsola {
  codigo: 'nomina' | 'multisucursal' | 'contabilidad'
  nombre: string
  contratado: boolean
  estado: 'activo' | 'suspendido' | null
  paquete: PaqueteNomina | null
  limiteEmpleados: number | null
  desde: string | null
  actualizadoEl: string | null
  /** Si el servidor tiene lo que el complemento necesita. La multisucursal siempre lo tiene. */
  servidorListo: boolean
  /** Solo la multisucursal: cuántas sucursales cubre. `null` es sin tope. */
  limiteSucursales?: number | null
  /** Solo la multisucursal: la última cifra que informó la caja. */
  sucursalesActivas?: number | null
  sucursalesInformadasEl?: string | null
}

export type PaqueteNomina = 'micro' | 'pyme' | 'pro' | 'empresa'

export function useComplementos(id: string) {
  return useQuery({
    queryKey: clavesConsola.subrecurso(id, 'complementos'),
    queryFn: ({ signal }) => clienteConsola.obtener<ComplementoConsola[]>(`${ruta(id)}/complementos`, { senal: signal }),
  })
}

export function useFijarNomina(id: string) {
  return useEscrituraTenant(
    id,
    (v: { estado: 'activo' | 'suspendido'; paquete: PaqueteNomina | null; limiteEmpleados: number | null; motivo: string }) =>
      clienteConsola.reemplazar(`${ruta(id)}/complementos/nomina`, { cuerpo: v }),
  )
}

export function useFijarMultisucursal(id: string) {
  return useEscrituraTenant(
    id,
    (v: { estado: 'activo' | 'suspendido'; limiteSucursales: number | null; motivo: string }) =>
      clienteConsola.reemplazar(`${ruta(id)}/complementos/multisucursal`, { cuerpo: v }),
  )
}

export function useFijarContabilidad(id: string) {
  return useEscrituraTenant(id, (v: { estado: 'activo' | 'suspendido'; motivo: string }) =>
    clienteConsola.reemplazar(`${ruta(id)}/complementos/contabilidad`, { cuerpo: v }),
  )
}

/** Fuera de `useMutation` por la credencial inicial, como el alta de cliente. */
export function agregarUsuario(
  id: string,
  v: { email: string; rol: RolUsuario; credencialInicial?: string; motivo: string },
): Promise<{ usuarioId: string }> {
  return clienteConsola.crear<{ usuarioId: string }>(`${ruta(id)}/usuarios`, { cuerpo: v })
}

export function useCambiarRol(id: string) {
  return useEscrituraTenant(id, (v: { usuarioId: string; rol: RolUsuario; motivo: string }) =>
    clienteConsola.reemplazar(`${ruta(id)}/usuarios/${encodeURIComponent(v.usuarioId)}/rol`, {
      cuerpo: { rol: v.rol, motivo: v.motivo },
    }),
  )
}

export function useRevocarUsuario(id: string) {
  return useEscrituraTenant(id, (v: { usuarioId: string; motivo: string }) =>
    clienteConsola.crear(`${ruta(id)}/usuarios/${encodeURIComponent(v.usuarioId)}/revocar`, {
      cuerpo: { motivo: v.motivo },
    }),
  )
}

/**
 * Bloquear y desbloquear son **globales**: la ruta no lleva contribuyente. Se
 * invalida el contribuyente desde el que se hizo porque es el que está en
 * pantalla, pero el efecto alcanza a todos los de esa cuenta.
 */
export function useBloqueoUsuario(idTenant: string) {
  return useEscrituraTenant(idTenant, (v: { usuarioId: string; bloquear: boolean; motivo: string }) =>
    clienteConsola.crear(
      `/consola/usuarios/${encodeURIComponent(v.usuarioId)}/${v.bloquear ? 'bloquear' : 'desbloquear'}`,
      { cuerpo: { motivo: v.motivo } },
    ),
  )
}

/**
 * La credencial nueva **no** pasa por `useMutation`: la caché de mutaciones
 * conserva las variables mientras la pantalla vive, y una credencial en claro
 * no tiene por qué quedarse en memoria más que lo que dura la petición.
 */
export function restablecerCredencial(usuarioId: string, credencialNueva: string, motivo: string): Promise<void> {
  return clienteConsola.crear<void>(`/consola/usuarios/${encodeURIComponent(usuarioId)}/credencial`, {
    cuerpo: { credencialNueva, motivo },
  })
}

export function useCrearSecuencia(id: string) {
  return useEscrituraTenant(
    id,
    (v: { tipoEcf: number; desde: number; hasta: number; autorizadaEl: string; venceEl?: string; motivo: string }) =>
      clienteConsola.crear<{ id: string }>(`${ruta(id)}/secuencias`, { cuerpo: v }),
  )
}

/**
 * La carga del certificado, por el mismo motivo que la credencial: la
 * contraseña del PFX va en el `FormData` de esta llamada y en ningún otro
 * sitio. Nada de `useMutation`, nada de caché. Quien llama refresca después.
 */
export function cargarCertificado(id: string, archivo: File, contrasena: string, motivo: string) {
  const formulario = new FormData()
  formulario.append('archivo', archivo, archivo.name)
  formulario.append('contrasena', contrasena)
  formulario.append('motivo', motivo)
  return clienteConsola.crear<CertificadoCargado>(`${ruta(id)}/certificados`, {
    cuerpo: formulario,
  })
}

/**
 * Generar una clave. Fuera de `useMutation` por lo mismo que el alta de cliente:
 * la respuesta trae el secreto, y la caché de mutaciones lo conservaría mientras
 * la pantalla vive. Quien llama lo enseña una vez y refresca con
 * `useRefrescarTenant`.
 */
export function generarClave(id: string, nombre: string, motivo: string): Promise<ClaveGenerada> {
  return clienteConsola.crear<ClaveGenerada>(`${ruta(id)}/claves`, { cuerpo: { nombre, motivo } })
}

export function useRevocarClave(id: string) {
  return useEscrituraTenant(id, (v: { claveId: string; motivo: string }) =>
    clienteConsola.crear(`${ruta(id)}/claves/${encodeURIComponent(v.claveId)}/revocar`, {
      cuerpo: { motivo: v.motivo },
    }),
  )
}

export function useRefrescarTenant(id: string): () => Promise<unknown> {
  const cliente = useQueryClient()
  return () => invalidarTenant(cliente, id)
}

export function useRevocarCertificado(id: string) {
  return useEscrituraTenant(id, (v: { certId: string; motivo: string }) =>
    clienteConsola.crear(`${ruta(id)}/certificados/${encodeURIComponent(v.certId)}/revocar`, {
      cuerpo: { motivo: v.motivo },
    }),
  )
}

/** Reinyectar: lo puede hacer también el rol `soporte`. */
export function useReinyectar() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (v: { tenantId: string; trabajoId: string; motivo: string }) =>
      clienteConsola.crear(
        `${ruta(v.tenantId)}/colas/${encodeURIComponent(v.trabajoId)}/reinyectar`,
        { cuerpo: { motivo: v.motivo } },
      ),
    onSuccess: (_resultado, v) => invalidarTenant(cliente, v.tenantId),
  })
}

export function useEntrarContingencia(id: string) {
  return useEscrituraTenant(id, (v: { situacion: string; motivo: string }) =>
    clienteConsola.crear(`${ruta(id)}/contingencia/entrada`, { cuerpo: v }),
  )
}

export function useSalirContingencia(id: string) {
  return useEscrituraTenant(id, (v: { motivo: string }) =>
    clienteConsola.crear(`${ruta(id)}/contingencia/salida`, { cuerpo: v }),
  )
}

export function abrirSoporte(id: string, modo: ModoSoporte, motivo: string): Promise<SesionSoporteAbierta> {
  return clienteConsola.crear<SesionSoporteAbierta>(`${ruta(id)}/soporte`, { cuerpo: { modo, motivo } })
}

export function cerrarSoporte(sesionId: string): Promise<void> {
  return clienteConsola.crear<void>(`/consola/soporte/${encodeURIComponent(sesionId)}/cerrar`)
}

/* ---------------------------------------------------------------------- */
/* Errores                                                                 */
/* ---------------------------------------------------------------------- */

export interface ErrorTraducido {
  titulo: string
  contexto?: string
  detalleTecnico?: string
}

/**
 * Traducción de errores de la consola a «qué pasó y qué hacer».
 *
 * El `detail` del servidor va como contexto porque en la consola es el dato:
 * «ya tiene comprobantes numerados», «el certificado es de otra persona». Lo
 * crudo queda plegado, para quien depura.
 */
export function traducirErrorConsola(
  fallo: unknown,
  queHacia: string,
  // Una lectura que falla no deja nada a medias; una escritura, quizá sí. Se
  // deduce del verbo porque todas las lecturas de la consola se piden como
  // «cargar …».
  lectura: boolean = queHacia.startsWith('cargar '),
): ErrorTraducido {
  if (!(fallo instanceof ErrorApi)) {
    return { titulo: `No se pudo ${queHacia}.`, detalleTecnico: fallo instanceof Error ? fallo.message : undefined }
  }

  const detalleTecnico = [fallo.estado ? `HTTP ${fallo.estado}` : null, fallo.codigo, fallo.crudo]
    .filter(Boolean)
    .join('\n')

  switch (fallo.clase) {
    case 'red':
      return { titulo: 'Sin conexión con el servidor.', contexto: 'No se hizo nada. Vuelve a intentarlo.', detalleTecnico }
    case 'autenticacion':
      return { titulo: 'La sesión de la consola terminó.', contexto: 'Vuelve a entrar para seguir.', detalleTecnico }
    case 'permiso':
      return {
        titulo: 'Tu rol no permite esto.',
        contexto: 'Esta acción es solo del dueño de la plataforma.',
        detalleTecnico,
      }
    case 'no-encontrado':
      return {
        titulo: `No se pudo ${queHacia}: el servidor no lo encuentra.`,
        contexto:
          fallo.estado === 404 && fallo.crudo === undefined
            ? 'Puede que este extremo de la consola todavía no exista en el servidor.'
            : fallo.message,
        detalleTecnico,
      }
    case 'conflicto':
      return { titulo: `No se pudo ${queHacia}.`, contexto: fallo.message, detalleTecnico }
    case 'validacion':
      return { titulo: `No se pudo ${queHacia}: hay datos que revisar.`, contexto: fallo.message, detalleTecnico }
    case 'servidor': {
      // 502, 503 y 504 los pone quien está delante de la API: la API no contestó.
      const noContesta = fallo.estado >= 502 && fallo.estado <= 504
      return {
        titulo: noContesta ? `No se pudo ${queHacia}: la API no responde.` : `El servidor falló al ${queHacia}.`,
        // En una escritura no se promete nada: un 504 puede llegar después de
        // que la API ya aplicó el cambio.
        contexto: lectura
          ? 'Reintenta en un momento. Si sigue, el detalle técnico dice qué contestó.'
          : 'No sabemos si llegó a aplicarse. Revisa el estado antes de repetirlo.',
        detalleTecnico,
      }
    }
    default:
      return { titulo: `No se pudo ${queHacia}.`, contexto: fallo.message, detalleTecnico }
  }
}
